import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { barberProfileApi } from '../services/barberProfileApi';
import subscriptionAddOnService from '../services/subscriptionAddOnService';
import { AddOnType } from '../types/subscription';
import { INVENTORY_CHANGED_EVENT } from './useInventoryAccess';

export interface InventoryAccessState {
  /** Whether user has subscribed to the Inventory add-on */
  hasSubscription: boolean;
  /** Whether user has enabled inventory in their profile */
  hasProfileEnabled: boolean;
  /** Whether user has full access (both subscription and profile enabled) */
  hasFullAccess: boolean;
  /** Whether access check is still loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Custom hook to check if the current user has full Inventory access
 * This checks BOTH:
 * 1. User has an active Inventory add-on subscription
 * 2. User has inventoryEnabled: true in their barber profile
 *
 * @returns InventoryAccessState with detailed access information
 */
export const useFullInventoryAccess = (): InventoryAccessState => {
  const { state } = useAuth();
  const [accessState, setAccessState] = useState<InventoryAccessState>({
    hasSubscription: false,
    hasProfileEnabled: false,
    hasFullAccess: false,
    isLoading: true,
    error: null,
  });

  const checkAccess = useCallback(async () => {
    // Only check for Barber and ShopOwner roles
    if (!state.isAuthenticated || (state.user?.role !== 'Barber' && state.user?.role !== 'ShopOwner')) {
      setAccessState({
        hasSubscription: false,
        hasProfileEnabled: false,
        hasFullAccess: false,
        isLoading: false,
        error: null,
      });
      return;
    }

    try {
      // Check both conditions in parallel
      const [availableAddOns, profile] = await Promise.all([
        subscriptionAddOnService.getAvailableAddOns(),
        barberProfileApi.getMyProfile(),
      ]);

      // Find the Inventory add-on and check if subscribed
      const inventoryAddOn = availableAddOns.find(addOn => addOn.type === AddOnType.Inventory);
      const hasSubscription = inventoryAddOn?.isSubscribed === true;

      // Check if inventory is enabled in profile
      const hasProfileEnabled = profile.inventoryEnabled === true;

      setAccessState({
        hasSubscription,
        hasProfileEnabled,
        hasFullAccess: hasSubscription && hasProfileEnabled,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error checking full inventory access:', error);
      setAccessState({
        hasSubscription: false,
        hasProfileEnabled: false,
        hasFullAccess: false,
        isLoading: false,
        error: 'Failed to check inventory access',
      });
    }
  }, [state.isAuthenticated, state.user?.role]);

  // Initial check on mount and when auth state changes
  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Listen for inventory setting change events
  useEffect(() => {
    const handleInventoryChange = () => {
      checkAccess();
    };

    window.addEventListener(INVENTORY_CHANGED_EVENT, handleInventoryChange);

    return () => {
      window.removeEventListener(INVENTORY_CHANGED_EVENT, handleInventoryChange);
    };
  }, [checkAccess]);

  return accessState;
};
